import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import ContributionHeatmap from '../components/ContributionHeatmap'
import { getRepoDetails, getRepoCommits, getRepoPullRequests, getRepoIssues } from '../utils/methods'
import { 
  GitFork, 
  Star, 
  ExternalLink, 
  GitCommit, 
  GitPullRequest, 
  AlertCircle,
  Code,
  Calendar,
  User,
  ArrowLeft
} from 'lucide-react'

export default function RepoDetails() {
  const { owner, name } = useParams()
  const [repo, setRepo] = useState(null)
  const [commits, setCommits] = useState([])
  const [pullRequests, setPullRequests] = useState([])
  const [issues, setIssues] = useState([])
  const [userCommits, setUserCommits] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    let cancelled = false

    const fetchRepoData = async () => {
      try {
        setLoading(true)
        const auth = JSON.parse(localStorage.getItem('auth'))

        // Fetch all data in parallel
        const [repoRes, commitsRes, prsRes, issuesRes] = await Promise.allSettled([
          getRepoDetails(owner, name),
          getRepoCommits(owner, name, { per_page: 100 }),
          getRepoPullRequests(owner, name, { state: 'open', per_page: 10 }),
          getRepoIssues(owner, name, { state: 'open', per_page: 10 })
        ])

        if (cancelled) return

        if (repoRes.status === 'fulfilled') setRepo(repoRes.value.data)
        if (commitsRes.status === 'fulfilled') setCommits(commitsRes.value.data)
        if (prsRes.status === 'fulfilled') setPullRequests(prsRes.value.data)
        if (issuesRes.status === 'fulfilled') setIssues(issuesRes.value.data)

        // Fetch user's commits if authenticated
        if (auth?.user?.username) {
          try {
            const userCommitsResponse = await getRepoCommits(owner, name, { 
              author: auth.user.username, 
              per_page: 20 
            })
            if (!cancelled) {
              setUserCommits(userCommitsResponse.data)
            }
          } catch (err) {
            console.error('Error fetching user commits:', err)
          }
        }

      } catch (error) {
        console.error('Error fetching repo data:', error)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchRepoData()

    return () => {
      cancelled = true
    }
  }, [owner, name])

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">Loading repository...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!repo) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">Repository not found</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Back Button */}
      <Link to="/dashboard">
        <Button variant="ghost" className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Button>
      </Link>

      {/* Repository Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <CardTitle className="text-xl sm:text-2xl">{repo.name}</CardTitle>
                {repo.private && (
                  <Badge variant="secondary">Private</Badge>
                )}
                {repo.fork && (
                  <Badge variant="outline">Fork</Badge>
                )}
              </div>
              {repo.description && (
                <CardDescription className="mt-2 text-base">{repo.description}</CardDescription>
              )}
              
              {/* Fork Info */}
              {repo.fork && repo.parent && (
                <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                  <GitFork className="h-4 w-4" />
                  <span>Forked from</span>
                  <a 
                    href={repo.parent.html_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {repo.parent.full_name}
                  </a>
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center gap-4 sm:gap-6 mt-4 flex-wrap">
                {repo.language && (
                  <span className="flex items-center gap-1 text-sm">
                    <div className="h-3 w-3 rounded-full bg-blue-500" />
                    {repo.language}
                  </span>
                )}
                <span className="flex items-center gap-1 text-sm">
                  <Star className="h-4 w-4" />
                  {repo.stargazers_count} stars
                </span>
                <span className="flex items-center gap-1 text-sm">
                  <GitFork className="h-4 w-4" />
                  {repo.forks_count} forks
                </span>
                <span className="flex items-center gap-1 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {repo.open_issues_count} issues
                </span>
              </div>
            </div>

            <Button asChild className="w-full sm:w-auto shrink-0">
              <a href={repo.html_url} target="_blank" rel="noopener noreferrer" className="gap-2">
                <ExternalLink className="h-4 w-4" />
                View on GitHub
              </a>
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Tab Navigation */}
      <div className="border-b">
        <div className="flex gap-4">
          {['overview', 'commits', 'pulls', 'issues', 'activity'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium transition-colors border-b-2 capitalize ${
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'activity' ? 'Your Activity' : tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Repository Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold">{repo.stargazers_count}</div>
                  <div className="text-sm text-muted-foreground">Stars</div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold">{repo.forks_count}</div>
                  <div className="text-sm text-muted-foreground">Forks</div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold">{repo.open_issues_count}</div>
                  <div className="text-sm text-muted-foreground">Open Issues</div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold">{repo.watchers_count}</div>
                  <div className="text-sm text-muted-foreground">Watchers</div>
                </div>
              </div>
              
              <div className="mt-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Created:</span>
                  <span>{new Date(repo.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Last Updated:</span>
                  <span>{new Date(repo.updated_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Default Branch:</span>
                  <span>{repo.default_branch}</span>
                </div>
                {repo.license && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">License:</span>
                    <span>{repo.license.name}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'commits' && (
        <div className="space-y-6">
          {/* Heatmap */}
          <Card>
            <CardContent className="pt-6">
              <ContributionHeatmap 
                commits={commits} 
                title="Repository Commit Activity (Last 12 Months)" 
              />
            </CardContent>
          </Card>

          {/* Commits List */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Commits</CardTitle>
              <CardDescription>Latest commits to this repository (showing all contributors)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {commits.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No commits found</p>
                ) : (
                  commits.slice(0, 20).map((commit) => (
                    <div key={commit.sha} className="border rounded-lg p-4 hover:bg-accent transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <GitCommit className="h-4 w-4 text-muted-foreground" />
                            <h3 className="font-medium">{commit.commit.message.split('\n')[0]}</h3>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {commit.commit.author.name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(commit.commit.author.date).toLocaleDateString()}
                            </span>
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {commit.sha.substring(0, 7)}
                            </code>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" asChild>
                          <a href={commit.html_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'pulls' && (
        <Card>
          <CardHeader>
            <CardTitle>Open Pull Requests</CardTitle>
            <CardDescription>Active pull requests in this repository</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pullRequests.length === 0 ? (
                <p className="text-sm text-muted-foreground">No open pull requests</p>
              ) : (
                pullRequests.map((pr) => (
                  <div key={pr.id} className="border rounded-lg p-4 hover:bg-accent transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <GitPullRequest className="h-4 w-4 text-green-500" />
                          <h3 className="font-medium">{pr.title}</h3>
                          <Badge variant="secondary" className="text-xs">#{pr.number}</Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {pr.user.login}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(pr.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" asChild>
                        <a href={pr.html_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'issues' && (
        <Card>
          <CardHeader>
            <CardTitle>Open Issues</CardTitle>
            <CardDescription>Active issues in this repository</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {issues.length === 0 ? (
                <p className="text-sm text-muted-foreground">No open issues</p>
              ) : (
                issues.map((issue) => (
                  <div key={issue.id} className="border rounded-lg p-4 hover:bg-accent transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-orange-500" />
                          <h3 className="font-medium">{issue.title}</h3>
                          <Badge variant="secondary" className="text-xs">#{issue.number}</Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {issue.user.login}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(issue.created_at).toLocaleDateString()}
                          </span>
                          {issue.labels.length > 0 && (
                            <div className="flex gap-1">
                              {issue.labels.slice(0, 3).map((label) => (
                                <Badge 
                                  key={label.id} 
                                  variant="outline" 
                                  className="text-xs"
                                  style={{ borderColor: `#${label.color}` }}
                                >
                                  {label.name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" asChild>
                        <a href={issue.html_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'activity' && (
        <div className="space-y-6">
          {/* User Heatmap */}
          {userCommits.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <ContributionHeatmap 
                  commits={userCommits} 
                  title="Your Contribution Activity (Last 12 Months)" 
                />
              </CardContent>
            </Card>
          )}

          {/* User Commits List */}
          <Card>
            <CardHeader>
              <CardTitle>Your Activity</CardTitle>
              <CardDescription>Your commits on this repository</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {userCommits.length === 0 ? (
                  <p className="text-sm text-muted-foreground">You haven't made any commits to this repository yet</p>
                ) : (
                  userCommits.map((commit) => (
                    <div key={commit.sha} className="border rounded-lg p-4 hover:bg-accent transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <GitCommit className="h-4 w-4 text-primary" />
                            <h3 className="font-medium">{commit.commit.message.split('\n')[0]}</h3>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(commit.commit.author.date).toLocaleDateString()}
                            </span>
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {commit.sha.substring(0, 7)}
                            </code>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" asChild>
                          <a href={commit.html_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
